import { Text } from "@imminently/react/ui/core/text";
import { Divider, Stack } from "@mui/material";
import { Fragment } from "react";
import { obsMetricLabelToObsMetric } from "@/resources/observations_metricUtils";
import { css, Theme } from "@/theme";
import { SvgChart } from "../SvgChart";
import { useObsChartCtx } from "../ObservationChart/ObservationChart_ctx";
import { getObsChartLineColor } from "../ObservationChart/ObservationChart";
import { useObsTabsCtx } from "../ObservationChart/ObservationTabs_ctx";
import { getItemsForSummary } from ".";
import { Observation } from "@/resources/types";

const DETAIL_CSS = {
  mini: css`
    > .MuiTypography-body1 {
      font-size: 0.75rem;
    }

    > div {
      .MuiTypography-h3 {
        font-size: 1.25rem;
      }
      .MuiTypography-body1 {
        font-size: 0.75rem;
      }
    }
  `,
  graph: (theme: Theme) => css`
    color: ${theme.palette.success.main};
    height: 18px; // Add height to chart to prevent top from cutting off
    margin-bottom: 0.3rem;
  `,
};

export type DetailProps = {
  label: string;
  mini: boolean;
  graphData?: number[];
  value?: string | number;
  units?: string;
  showChartLineColors?: boolean;
};

// An individual observation in the summary
const Detail = ({ value, units, label, mini, graphData, showChartLineColors }: DetailProps) => {
  const { isActive: isChartActive } = useObsChartCtx().value;
  const { value: { tab: type } } = useObsTabsCtx();
  const showActiveColor = !(Boolean(showChartLineColors) === false || isChartActive == false);
  return (
    <Stack css={mini && DETAIL_CSS.mini} alignItems="center" justifyContent="end">
      {graphData && (
        <SvgChart
          css={DETAIL_CSS.graph}
          width={32}
          height={16}
          data={graphData}
        />
      )}

      {value ? (
        <Stack direction="row" alignItems="baseline">
          <Text variant="h3" mr={0.5}>{value}</Text>
          <Text>{units}</Text>
        </Stack>
      ) : (
        <Text variant="h4">-</Text>
      )}

      <Text>{label}</Text>

      {showActiveColor && (
        <Stack
          width='100%'
          marginTop='0.5rem'
          borderBottom={`5px solid ${getObsChartLineColor(obsMetricLabelToObsMetric(label), type)}`}
        />
      )}
    </Stack>
  );
};


export type ObservationSummaryProps = {
  observations: Observation[];
  wrap?: boolean; // Allow the summary to wrap if width exceeds container
  align?: string; // Alignment of component. Takes values for `justify-content`
  mini?: boolean; // Toggle the mini variant of this summary
  showGraphs?: boolean; // Toggle graphs that show how each observation changed over time
  showChartLineColors?: boolean // show colored lines that correspond to line colors in ObservationChart component
};

const ObservationSummary = (props: ObservationSummaryProps) => {
  const {
    observations,
    align,
    wrap = false,
    mini = false,
    showGraphs = false,
    showChartLineColors = false,
  } = props;
  const { value: { tab: type } } = useObsTabsCtx();

  const items = getItemsForSummary({ observations, showGraphs, type });
  // Join items with a <Divider ..> component between them
  return (
    <Stack
      direction="row"
      flexWrap={wrap ? "wrap" : undefined}
      justifyContent={align || "space-around"}
      gap={mini ? "0.7rem" : undefined}
    >
      {items.map((item, index) => (
        <Fragment key={index}>
          <Detail mini={mini} showChartLineColors={showChartLineColors} {...item} />

          {index < items.length - 1 && (
            <Divider orientation="vertical" flexItem variant="middle" />
          )}
        </Fragment>
      ))}
    </Stack>
  );
};

export default ObservationSummary;
